import { onBeforeUnmount } from 'vue';

type EmitCallback = (event: string) => void;

let trigger: HTMLElement | null;

export default function useModalFocusTrap(
	modalRoot: HTMLElement,
	emit: EmitCallback,
	closeEvent: string,
	closeOnEscape: boolean
) {
	if (!modalRoot) {
		console.error('This component needs a root element. Make sure the component is mounted before initializing this composable.');
		return {
			restoreFocus: () => {},
			updateTrigger: () => {},
			focus: () => {},
		};
	}

	trigger =  document.activeElement as HTMLElement;

	let focusableElements: NodeListOf<HTMLElement>;
	let firstFocusableElement: HTMLElement;
	let lastFocusableElement: HTMLElement;

	focusableElements = modalRoot.querySelectorAll('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
	firstFocusableElement = focusableElements[0];
	lastFocusableElement = focusableElements[focusableElements.length - 1];

	if (firstFocusableElement) {
		firstFocusableElement.focus();
	}
	const observer = new MutationObserver(() => {
		focusableElements = modalRoot.querySelectorAll('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
		firstFocusableElement = focusableElements[0];
		lastFocusableElement = focusableElements[focusableElements.length - 1];
		if (document.activeElement && !modalRoot.contains(document.activeElement) && focusableElements.length > 0) {
			focusableElements[0].focus();
		}
	});

	observer.observe(modalRoot, {
		subtree: true,
		childList: true,
	});

	const focus = () => {
		if (firstFocusableElement) {
			firstFocusableElement.focus();
		}
	}

	const restoreFocus = () => {
		trigger?.focus();
	}

	const updateTrigger = (newTrigger: HTMLElement | null) => {
		trigger = newTrigger;
	}

	const closeModal = () => {
		restoreFocus();
		emit(closeEvent);
		observer.disconnect();
		modalRoot.removeEventListener('keydown', tabKeyHandler);
	}

	const tabKeyHandler = (e: KeyboardEvent) => {
		const escapeWasPressed = e.key === 'Escape';
		if (escapeWasPressed && closeOnEscape) {
			closeModal();
			return;
		}

		const tabWasPressed = e.key === 'Tab';
		if (!tabWasPressed && !escapeWasPressed) {
			return;
		}

		if (e.shiftKey && document.activeElement === firstFocusableElement) {
			e.preventDefault();
			lastFocusableElement.focus();
		}
		else if (!e.shiftKey && document.activeElement === lastFocusableElement) {
			e.preventDefault();
			firstFocusableElement.focus();
		}
	}

	modalRoot.addEventListener('keydown', tabKeyHandler);

	onBeforeUnmount(() => {
		observer.disconnect();
		modalRoot.removeEventListener('keydown', tabKeyHandler);
	});

	return {
		restoreFocus,
		updateTrigger,
		focus,
	};
}
